import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_IMAGE = "/images/fp-hero.webp";

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden bg-white"
    >
      {/* Ambient gradient orbs */}
      <div className="pointer-events-none absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#0055FF]/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-24 w-[420px] h-[420px] rounded-full bg-[#FF7A00]/15 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-6"
        >
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neutral-900 text-white text-xs font-semibold tracking-wide"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#FF7A00]" />
            {t.hero.badge}
          </div>

          <h1
            data-testid="hero-title"
            className="mt-6 font-heading text-[42px] leading-[1.02] sm:text-6xl lg:text-[76px] font-bold tracking-tight text-neutral-900"
          >
            {t.hero.title1}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#0055FF]">{t.hero.title2}</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-[#FF7A00]/25 -skew-x-6" />
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-lg md:text-xl text-neutral-600 leading-relaxed max-w-xl"
          >
            {t.hero.subtitle}
          </p>

          <p
            data-testid="hero-inout"
            className="mt-3 text-sm md:text-base font-medium text-neutral-500"
          >
            {t.hero.inOut}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={t.hero.cta1Href || "#events"}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0055FF] text-white px-6 py-3.5 text-sm md:text-base font-semibold hover:bg-[#0044dd] transition-all shadow-xl shadow-[#0055FF]/25"
            >
              {t.hero.cta1}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={t.hero.cta2Href || "#branding"}
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-neutral-900 border border-neutral-200 px-6 py-3.5 text-sm md:text-base font-semibold hover:bg-neutral-50 transition-all"
            >
              <PlayCircle className="w-4 h-4 text-[#0055FF]" />
              {t.hero.cta2}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          className="lg:col-span-6"
        >
          <div className="relative rounded-[32px] overflow-hidden shadow-2xl shadow-neutral-900/20 border border-white/40 aspect-[4/5] md:aspect-[5/6] lg:aspect-[4/5]">
            <img
              data-testid="hero-image"
              src={HERO_IMAGE}
              alt={t.hero.imgAlt}
              width={1024}
              height={1024}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "50% 45%" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-neutral-900/10 to-transparent" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
